import React, { useState } from "react";
import { supabase } from "@core/lib/supabase";
import { invokeSupabaseFunction } from "@core/lib/supabaseApi";
import {
  Brain,
  Send,
  Edit2,
  Trash2,
  ExternalLink,
  Check,
  X,
} from "lucide-react";
import {
  SupportMessage,
  SupportTicket,
  supportTicketService,
} from "@core/services/SupportTicketService";

interface DraftMessageCardProps {
  draft: SupportMessage;
  ticket: SupportTicket;
  onSent: (message: SupportMessage) => void;
  onEdited: (message: SupportMessage) => void;
  onRejected: (messageId: string) => void;
}

export const DraftMessageCard: React.FC<DraftMessageCardProps> = ({
  draft,
  ticket,
  onSent,
  onEdited,
  onRejected,
}) => {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(draft.content);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kind = draft.content_attributes?.draft_kind;
  const prUrl = draft.content_attributes?.pr_url;

  const kindLabel =
    kind === "fix"
      ? "Fix proposé"
      : kind === "info-request"
        ? "Demande d'info"
        : "Brouillon";

  const handleSaveEdit = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await supportTicketService.editDraft(draft.id, content);
      onEdited(updated);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur d'édition");
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    setBusy(true);
    setError(null);
    try {
      // 1. Envoyer l'email via la Edge Function (admin JWT requis)
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Session admin requise pour envoyer l'email");
      }

      try {
        await invokeSupabaseFunction(
          "send-support-reply-email",
          {
            ticketId: ticket.id,
            ticketSubject: ticket.subject,
            userEmail: ticket.email,
            agentMessage: content,
            ticketUrl: `https://doodates.com/admin/support?ticket=${ticket.id}`,
          },
          { timeout: 15000 },
        );
      } catch (e) {
        console.warn("Email non envoyé:", e);
        // On continue quand même — le draft passe en 'sent' pour trace,
        // l'admin peut retenter manuellement si besoin.
      }

      // 2. Transition draft -> sent (UPDATE status). Si le contenu a été
      //    édité sans clic sur "Enregistrer" avant d'envoyer, on le
      //    persiste d'abord.
      if (editing && content !== draft.content) {
        await supportTicketService.editDraft(draft.id, content);
      }
      const sent = await supportTicketService.sendDraft(draft.id);
      onSent(sent);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur d'envoi");
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm("Rejeter ce brouillon ? Il sera supprimé.")) return;
    setBusy(true);
    setError(null);
    try {
      await supportTicketService.rejectDraft(draft.id);
      onRejected(draft.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur de rejet");
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-purple-900/20 border border-purple-500/40 rounded-xl p-3"
      data-testid="draft-message-card"
      data-draft-id={draft.id}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-purple-400" />
          <span className="text-xs font-semibold text-purple-300 uppercase tracking-wider">
            Brouillon IA — {kindLabel}
          </span>
          {prUrl && (
            <a
              href={prUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-purple-300 hover:text-purple-200 underline flex items-center gap-0.5"
            >
              PR <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        <span className="text-[10px] text-gray-500">
          {new Date(draft.created_at).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>

      {editing ? (
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={busy}
          className="w-full bg-black/30 border border-purple-500/30 rounded-lg p-2 text-sm text-gray-100 resize-y min-h-[100px] focus:outline-none focus:border-purple-400"
          data-testid="draft-edit-textarea"
        />
      ) : (
        <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">
          {content}
        </p>
      )}

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}

      <div className="flex items-center gap-2 mt-3 justify-end">
        {editing ? (
          <>
            <button
              onClick={() => {
                setContent(draft.content);
                setEditing(false);
                setError(null);
              }}
              disabled={busy}
              className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 rounded disabled:opacity-50 flex items-center gap-1"
              data-testid="draft-cancel-edit"
            >
              <X className="w-3 h-3" /> Annuler
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={busy || content.trim() === ""}
              className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded disabled:opacity-50 flex items-center gap-1"
              data-testid="draft-save-edit"
            >
              <Check className="w-3 h-3" /> Enregistrer
            </button>
          </>
        ) : (
          <button
            onClick={() => setEditing(true)}
            disabled={busy}
            className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 rounded disabled:opacity-50 flex items-center gap-1"
            data-testid="draft-edit"
          >
            <Edit2 className="w-3 h-3" /> Éditer
          </button>
        )}
        <button
          onClick={handleReject}
          disabled={busy}
          className="px-2 py-1 text-xs bg-red-700/60 hover:bg-red-600 text-red-100 rounded disabled:opacity-50 flex items-center gap-1"
          data-testid="draft-reject"
        >
          <Trash2 className="w-3 h-3" /> Rejeter
        </button>
        <button
          onClick={handleSend}
          disabled={busy || content.trim() === ""}
          className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-500 text-white rounded disabled:opacity-50 flex items-center gap-1 font-semibold"
          data-testid="draft-send"
        >
          <Send className="w-3 h-3" /> Envoyer
        </button>
      </div>
    </div>
  );
};
